import { useRef } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  getAppSettings,
  getLiveStatusForTeam,
  getPublicTeam,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { unscopedKey } from "@/lib/queryClient";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useAirPlay } from "@/features/playback/useAirPlay";
import { useChromecast } from "@/features/playback/useChromecast";

/**
 * `/teams/$teamSlug/live` — per-team public live viewer (M12c).
 *
 * Mirrors the M11b global `LiveShell` flow but reads the per-team status
 * (`/api/live/status?team_slug=<slug>`) and points `<HlsVideo>` at the
 * per-team HLS proxy (`/api/live/hls/teams/<slug>/...`). The team-slug
 * is in the URL; the stream key never is.
 *
 * Public, anonymous-safe. Non-public teams 404 (both the public-team
 * lookup AND the status query). The CTA on `/teams/$teamSlug` now
 * routes here instead of the legacy global `/live`.
 */
export function TeamLivePage() {
  const { teamSlug } = useParams({ from: "/teams/$teamSlug/live" });

  const teamQuery = useQuery({
    queryKey: unscopedKey("public-team", { slug: teamSlug }),
    queryFn: () => getPublicTeam(teamSlug),
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const settingsQuery = useQuery({
    queryKey: unscopedKey("app-settings"),
    queryFn: getAppSettings,
    staleTime: 5 * 60_000,
  });
  const settings = settingsQuery.data?.settings;
  const settingsLiveEnabled = settings ? coerceBool(settings.live_enabled) : true;

  const statusQuery = useQuery({
    queryKey: unscopedKey("team-live-status", { slug: teamSlug }),
    queryFn: () => getLiveStatusForTeam(teamSlug),
    enabled: settingsLiveEnabled && !!teamQuery.data,
    refetchInterval: settingsLiveEnabled ? 4_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  const status = statusQuery.data;

  const team = teamQuery.data;
  useDocumentMeta({
    title: team ? `Live · ${team.name}` : "Live · tktic",
    description: team ? `Live broadcast from ${team.name}.` : undefined,
    ogType: "video.other",
  });

  if (teamQuery.isLoading) {
    return (
      <main
        data-testid="team-live"
        className="min-h-[40vh] bg-canvas text-ink"
      >
        <div className="mx-auto max-w-5xl px-6 py-10">
          <p
            data-testid="team-live-loading"
            className="font-mono text-sm text-ink-muted"
          >
            Loading team…
          </p>
        </div>
      </main>
    );
  }

  if (teamQuery.isError) {
    const notFound =
      teamQuery.error instanceof ApiError && teamQuery.error.status === 404;
    return (
      <main
        data-testid="team-live"
        className="min-h-[40vh] bg-canvas text-ink"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-6 py-12">
          <p
            data-testid={notFound ? "team-live-404" : "team-live-error"}
            className="font-display text-2xl text-ink"
          >
            {notFound
              ? "Team not found"
              : "Couldn’t load this team. Please refresh in a moment."}
          </p>
          <Link
            to="/teams"
            data-testid="team-live-back"
            className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
          >
            ← back to teams
          </Link>
        </div>
      </main>
    );
  }

  if (!team) return null;

  const isDisabled = !settingsLiveEnabled || status?.enabled === false;
  const isActive = !isDisabled && !!(status?.active && status?.ready);
  const offlineMessage =
    settings?.live_offline_message || "No live stream right now.";

  return (
    <main
      data-testid="team-live"
      data-team-slug={team.slug}
      data-live-state={
        isDisabled ? "disabled" : isActive ? "live" : "offline"
      }
      className="min-h-screen bg-canvas text-ink"
    >
      <header className="mx-auto flex max-w-7xl items-center justify-between gap-3 border-b border-rule px-6 py-3">
        <div className="flex items-center gap-3">
          <Link
            to="/teams/$teamSlug"
            params={{ teamSlug: team.slug }}
            data-testid="team-live-breadcrumb"
            className="font-mono text-xs uppercase tracking-mono text-ink-muted hover:text-accent"
          >
            ← {team.name}
          </Link>
          <h1 className="font-display text-sm uppercase tracking-wider text-ink">
            now streaming
          </h1>
          <StatusPill
            state={isDisabled ? "disabled" : isActive ? "live" : "offline"}
          />
        </div>
      </header>

      <section className="mx-auto flex max-w-5xl flex-col gap-3 px-6 py-6">
        {isDisabled ? (
          <DisabledPlaceholder />
        ) : isActive ? (
          <TeamLivePlayer team={team} />
        ) : (
          <OfflinePlaceholder
            message={offlineMessage}
            reachable={status?.reachable !== false}
          />
        )}
      </section>
    </main>
  );
}

function StatusPill({ state }: { state: "disabled" | "live" | "offline" }) {
  if (state === "live") {
    return (
      <span
        data-testid="team-live-status-pill"
        data-state="live"
        className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-white"
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse"
          aria-hidden="true"
        />
        LIVE
      </span>
    );
  }
  if (state === "disabled") {
    return (
      <span
        data-testid="team-live-status-pill"
        data-state="disabled"
        className="inline-flex items-center rounded-full border border-rule bg-bg-card px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
      >
        Disabled
      </span>
    );
  }
  return (
    <span
      data-testid="team-live-status-pill"
      data-state="offline"
      className="inline-flex items-center rounded-full border border-rule bg-bg-card px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
    >
      Offline
    </span>
  );
}

function TeamLivePlayer({ team }: { team: { slug: string; name: string } }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsUrl = `/api/live/hls/teams/${encodeURIComponent(team.slug)}/index.m3u8`;
  const { available: airplayAvailable, casting, prompt: openAirplay } =
    useAirPlay(videoRef);
  const chromecast = useChromecast(videoRef, () => ({
    url: hlsUrl,
    contentType: "application/x-mpegURL",
    streamType: "LIVE",
    title: `${team.name} · live`,
    subtitle: "Live now",
  }));

  return (
    <div data-testid="team-live-player" className="flex flex-col gap-2">
      <HlsVideo
        ref={videoRef}
        hlsUrl={hlsUrl}
        mp4Url=""
        live
        autoPlay
        controls
        className="aspect-video w-full rounded bg-black"
        data-testid="team-live-video"
      />
      <div className="flex items-center justify-between gap-2 font-mono text-xs text-ink-muted">
        <span>Streaming from the {team.name} sideline.</span>
        {airplayAvailable || chromecast.supportedBrowser ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {airplayAvailable ? (
              <button
                type="button"
                onClick={openAirplay}
                data-testid="team-live-airplay-btn"
                data-casting={casting ? "true" : undefined}
                className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white"
              >
                AirPlay{casting ? " · ON" : ""}
              </button>
            ) : null}
            {chromecast.supportedBrowser ? (
              <button
                type="button"
                onClick={chromecast.toggle}
                disabled={!chromecast.sdkReady}
                title={chromecast.unavailableReason || undefined}
                data-testid="team-live-chromecast-btn"
                data-casting={chromecast.casting ? "true" : undefined}
                className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cast{chromecast.casting ? ` · ${chromecast.deviceName || "ON"}` : ""}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DisabledPlaceholder() {
  return (
    <div
      data-testid="team-live-disabled-placeholder"
      className="flex aspect-video flex-col items-center justify-center gap-2 rounded border border-rule bg-bg-card p-6 text-center text-ink-muted"
    >
      <p className="font-display text-sm uppercase tracking-wider">
        Live streaming is disabled
      </p>
      <p className="font-mono text-xs">
        An admin has turned off live streaming in app settings.
      </p>
    </div>
  );
}

function OfflinePlaceholder({
  message,
  reachable,
}: {
  message: string;
  reachable: boolean;
}) {
  return (
    <div
      data-testid="team-live-offline-placeholder"
      className="flex aspect-video flex-col items-center justify-center gap-2 rounded border border-rule bg-bg-card p-6 text-center text-ink-muted"
    >
      <p className="font-display text-sm uppercase tracking-wider">
        Stream offline
      </p>
      <p className="font-mono text-xs">{message}</p>
      {!reachable ? (
        <p className="font-mono text-[0.65rem] text-destructive">
          Streaming server is unreachable.
        </p>
      ) : null}
    </div>
  );
}
